import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { searchApi } from '../../api/search.js';
import { pmApi } from '../../api/pm.js';

/**
 * Cross-project search page.
 *
 * Filter sidebar (left) + result list (right). All filters are reflected
 * in the URL query string so a copied URL preserves the exact filter
 * state. Saving a filter persists the (current) URL params under a name
 * so it can be reloaded later from "My filters".
 */

const PRIORITY_LIST = ['critical', 'high', 'medium', 'low'];
const TYPE_LIST = ['story', 'task', 'bug', 'improvement', 'spike', 'subtask'];
const STATUS_LIST = ['todo', 'in_progress', 'in_review', 'done'];
const SORT_OPTIONS = [
  { value: 'updated_at:desc', label: 'Recently updated' },
  { value: 'created_at:desc', label: 'Recently created' },
  { value: 'priority:asc', label: 'Priority' },
  { value: 'due_date:asc', label: 'Due date' },
];

function paramsToFilters(sp) {
  const f = {};
  for (const [k, v] of sp.entries()) f[k] = v;
  return f;
}

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => paramsToFilters(searchParams), [searchParams]);

  const [results, setResults] = useState({ items: [], total: 0, has_more: false });
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [projects, setProjects] = useState([]);
  const [savedFilters, setSavedFilters] = useState([]);
  const debounceRef = useRef(null);

  // Cached projects for the project filter
  useEffect(() => {
    pmApi.projects().then((d) => setProjects(d?.items || d || [])).catch(() => setProjects([]));
    searchApi.listSaved().then((d) => setSavedFilters(d?.items || [])).catch(() => setSavedFilters([]));
  }, []);

  // Run search whenever filters change. Debounce 300ms on text input.
  useEffect(() => {
    const isTextChange = filters.q !== undefined;
    const delay = isTextChange ? 300 : 0;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      runSearch({ ...filters, page: 1 });
      setPage(1);
    }, delay);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filters)]);

  async function runSearch(params) {
    setLoading(true);
    try {
      const res = await searchApi.search(params);
      if (params.page > 1) {
        setResults((r) => ({ ...res, items: [...r.items, ...(res?.items || [])] }));
      } else {
        setResults(res || { items: [], total: 0 });
      }
    } catch {
      setResults({ items: [], total: 0, has_more: false });
    } finally {
      setLoading(false);
    }
  }

  // ── Filter setters (URL-driven) ───────────────────────────────────
  const updateFilter = useCallback((key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value === '' || value == null) next.delete(key);
    else next.set(key, value);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const toggleMulti = useCallback((key, value) => {
    const next = new URLSearchParams(searchParams);
    const cur = new Set(String(next.get(key) || '').split(',').filter(Boolean));
    if (cur.has(value)) cur.delete(value); else cur.add(value);
    if (cur.size === 0) next.delete(key);
    else next.set(key, [...cur].join(','));
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const clearAll = useCallback(() => setSearchParams({}, { replace: true }), [setSearchParams]);

  // Active filter pills (every filter except q + sort)
  const activePills = useMemo(() => {
    const pills = [];
    for (const [k, v] of searchParams.entries()) {
      if (k === 'q' || k === 'sort' || k === 'page') continue;
      String(v).split(',').forEach((value) => pills.push({ key: k, value }));
    }
    return pills;
  }, [searchParams]);

  function removePill(pill) {
    const next = new URLSearchParams(searchParams);
    const cur = String(next.get(pill.key) || '').split(',').filter(Boolean);
    const remaining = cur.filter((v) => v !== pill.value);
    if (remaining.length === 0) next.delete(pill.key);
    else next.set(pill.key, remaining.join(','));
    setSearchParams(next, { replace: true });
  }

  // Infinite scroll
  function loadMore() {
    if (!results.has_more || loading) return;
    const nextPage = page + 1;
    setPage(nextPage);
    runSearch({ ...filters, page: nextPage });
  }

  // Save current filter
  async function onSave() {
    const name = window.prompt('Name this filter:');
    if (!name) return;
    await searchApi.saveFilter({ name, filters });
    const list = await searchApi.listSaved();
    setSavedFilters(list?.items || []);
  }

  function loadSaved(s) {
    const next = new URLSearchParams();
    for (const [k, v] of Object.entries(s.filters || {})) next.set(k, v);
    setSearchParams(next, { replace: true });
  }

  async function deleteSaved(id) {
    if (!window.confirm('Delete this saved filter?')) return;
    await searchApi.deleteSaved(id);
    setSavedFilters((list) => list.filter((s) => s.id !== id));
  }

  const checkedSet = (key) => new Set(String(filters[key] || '').split(',').filter(Boolean));

  return (
    <section className="search-page" style={{ padding: 0 }}>
      <header className="page-header" style={{ padding: '16px 20px' }}>
        <h2>Search issues</h2>
        <button className="btn ghost small" onClick={onSave}>Save current filter</button>
      </header>

      <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', minHeight: '70vh' }}>
        {/* ── Filter sidebar ── */}
        <aside style={{ borderRight: '1px solid #e2e8f0', padding: 16, background: '#f8fafc', overflowY: 'auto' }}>
          {savedFilters.length > 0 && (
            <FilterGroup title="My filters">
              {savedFilters.map((s) => (
                <div key={s.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12, padding: '4px 0' }}>
                  <a href="#" onClick={(e) => { e.preventDefault(); loadSaved(s); }}>{s.name}</a>
                  <button className="btn ghost small" onClick={() => deleteSaved(s.id)} aria-label={`Delete ${s.name}`}>×</button>
                </div>
              ))}
            </FilterGroup>
          )}

          <FilterGroup title="Search text">
            <input
              type="search"
              placeholder="Title or description…"
              value={filters.q || ''}
              onChange={(e) => updateFilter('q', e.target.value)}
              style={{ width: '100%' }}
            />
          </FilterGroup>

          <FilterGroup title="Projects">
            {projects.map((p) => (
              <CheckRow
                key={p.id}
                checked={checkedSet('projects').has(p.key)}
                onChange={() => toggleMulti('projects', p.key)}
                label={`${p.key} — ${p.name}`}
              />
            ))}
          </FilterGroup>

          <FilterGroup title="Issue type">
            {TYPE_LIST.map((t) => (
              <CheckRow key={t} checked={checkedSet('type').has(t)} onChange={() => toggleMulti('type', t)} label={t} />
            ))}
          </FilterGroup>

          <FilterGroup title="Status">
            {STATUS_LIST.map((s) => (
              <CheckRow key={s} checked={checkedSet('status').has(s)} onChange={() => toggleMulti('status', s)} label={s} />
            ))}
          </FilterGroup>

          <FilterGroup title="Priority">
            {PRIORITY_LIST.map((p) => (
              <CheckRow key={p} checked={checkedSet('priority').has(p)} onChange={() => toggleMulti('priority', p)} label={<><span className={`prio-dot prio-${p}`} /> {p}</>} />
            ))}
          </FilterGroup>

          <FilterGroup title="Assignee">
            <input
              placeholder="user id, or 'me'"
              value={filters.assignee || ''}
              onChange={(e) => updateFilter('assignee', e.target.value)}
              style={{ width: '100%' }}
            />
          </FilterGroup>

          <FilterGroup title="Supervisor (creator's)">
            <input
              placeholder="supervisor user id"
              value={filters.supervisor_id || ''}
              onChange={(e) => updateFilter('supervisor_id', e.target.value)}
              style={{ width: '100%' }}
            />
          </FilterGroup>

          <FilterGroup title="Creator tag">
            <input
              placeholder="e.g. contractor"
              value={filters.tag || ''}
              onChange={(e) => updateFilter('tag', e.target.value)}
              style={{ width: '100%' }}
            />
          </FilterGroup>

          <FilterGroup title="Date ranges">
            <label style={{ fontSize: 11 }}>Created from</label>
            <input aria-label="Created from" type="date" value={filters.created_from || ''} onChange={(e) => updateFilter('created_from', e.target.value)} style={{ width: '100%' }} />
            <label style={{ fontSize: 11 }}>Created to</label>
            <input aria-label="Created to" type="date" value={filters.created_to || ''} onChange={(e) => updateFilter('created_to', e.target.value)} style={{ width: '100%' }} />
            <label style={{ fontSize: 11 }}>Due from</label>
            <input aria-label="Due from" type="date" value={filters.due_from || ''} onChange={(e) => updateFilter('due_from', e.target.value)} style={{ width: '100%' }} />
            <label style={{ fontSize: 11 }}>Due to</label>
            <input aria-label="Due to" type="date" value={filters.due_to || ''} onChange={(e) => updateFilter('due_to', e.target.value)} style={{ width: '100%' }} />
          </FilterGroup>

          <a href="#" onClick={(e) => { e.preventDefault(); clearAll(); }} style={{ fontSize: 12 }}>Clear all filters</a>
        </aside>

        {/* ── Results ── */}
        <main style={{ padding: 16, overflowY: 'auto' }}>
          {/* Active filter pills */}
          {activePills.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
              {activePills.map((pill, i) => (
                <span key={i} className="chip" style={{ fontSize: 11, padding: '4px 8px' }}>
                  {pill.key}: {pill.value}
                  <button onClick={() => removePill(pill)} style={{ marginLeft: 6, border: 'none', background: 'transparent', cursor: 'pointer' }} aria-label={`Remove filter ${pill.key}=${pill.value}`}>×</button>
                </span>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <span className="muted" data-testid="result-count">{results.total} {results.total === 1 ? 'issue' : 'issues'}</span>
            <select
              value={filters.sort || 'updated_at:desc'}
              onChange={(e) => updateFilter('sort', e.target.value)}
              aria-label="Sort"
            >
              {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          {loading && results.items.length === 0 && <div className="muted">Searching…</div>}

          {results.items.length === 0 && !loading && (
            <div className="muted" style={{ textAlign: 'center', padding: 32 }}>
              No issues match your filters. Try clearing filters or broadening the search.
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {results.items.map((it) => (
              <ResultCard key={it.id} issue={it} highlightQuery={filters.q || ''} />
            ))}
          </div>

          {results.has_more && (
            <div style={{ padding: 16, textAlign: 'center' }}>
              <button className="btn ghost" onClick={loadMore} disabled={loading}>
                {loading ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
        </main>
      </div>
    </section>
  );
}

function FilterGroup({ title, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function CheckRow({ checked, onChange, label }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, padding: '2px 0', cursor: 'pointer' }}>
      <input type="checkbox" checked={!!checked} onChange={onChange} />
      {label}
    </label>
  );
}

function ResultCard({ issue, highlightQuery }) {
  return (
    <Link to={`/pm/issues/${issue.key}`} className="card" style={{ display: 'block', padding: 12, textDecoration: 'none', color: 'inherit' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <code style={{ background: '#e2e8f0', padding: '2px 6px', borderRadius: 3, fontSize: 11 }}>{issue.key}</code>
        <span className="chip" style={{ fontSize: 10 }}>{issue.project_key}</span>
        <span className={`chip status-${issue.status}`} style={{ fontSize: 10 }}>{issue.status}</span>
        <span className={`chip priority-${issue.priority}`} style={{ fontSize: 10 }}>{issue.priority}</span>
        <div style={{ flex: 1, fontWeight: 600 }}>{highlight(issue.title, highlightQuery)}</div>
        <span className="muted" style={{ fontSize: 11 }}>{daysAgo(issue.updated_at)}</span>
      </div>
      {issue.assignee && (
        <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
          Assigned to {issue.assignee.name}
          {Array.isArray(issue.created_by_tags) && issue.created_by_tags.length > 0 && (
            <span> · creator tags: {issue.created_by_tags.join(', ')}</span>
          )}
        </div>
      )}
    </Link>
  );
}

function highlight(text, q) {
  if (!q) return text;
  const i = String(text).toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return text;
  return (<>{text.slice(0, i)}<strong>{text.slice(i, i + q.length)}</strong>{text.slice(i + q.length)}</>);
}

function daysAgo(iso) {
  if (!iso) return '';
  const days = Math.round((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days === 0) return 'today';
  if (days === 1) return '1d ago';
  return `${days}d ago`;
}
